import {
  issueFields,
  type IssueMetadata,
} from '@safc/configuration';
import { ErrorModal } from 'components/ui/modals/ErrorModal/ErrorModal.js';
import { useState } from 'react';
import type { FieldErrors } from 'react-hook-form';

export type ReactHookFormError = { [K in string]: ReactHookFormError } & { message?: string };

export type MappedError = { path: string[]; message: string };

const collectErrors = (error: ReactHookFormError, path: string[] = []): MappedError[] => {
  if ('message' in error && error.message) {
    return [{ path, message: error.message }];
  }

  return Object.entries(error)
    .flatMap(([key, value]) => collectErrors(value as ReactHookFormError, [...path, key]));
};

const serializeError = (error: MappedError): string => {
  const path = error.path.join('.');
  const maybeDescription = issueFields[path as keyof typeof issueFields]?.title;
  // we do not display:  — ${error.message}
  return `${maybeDescription ?? (error.message || path)}`;
};

export const useIssueFormErrors = () => {
  const [errors, setErrors] = useState<MappedError[]>([]);
  const [isErrorModalOpen, setErrorModalOpen] = useState<boolean>(false);

  const errorModal = (
    <ErrorModal
      errors={errors.map(serializeError)}
      open={isErrorModalOpen}
      onClose={() => setErrorModalOpen(false)}
    />
  );

  return {
    errorModal,
    setErrors: (errors: FieldErrors<IssueMetadata> | null) => {
      if (errors) {
        setErrors(collectErrors(errors as ReactHookFormError));
        setErrorModalOpen(true);
      } else {
        setErrors([]);
        setErrorModalOpen(false);
      }
    },
    hasTabError: (pathSegment: string) => errors.some(err => err.path.at(0) === pathSegment),
  };
};
